import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { WorkPackageView, CategorizedViews, ViewCategory } from '../../../models/work-package-view.model';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';

export interface ViewsListDialogData {
  views: WorkPackageView[];
  categorizedViews: CategorizedViews;
  activeView: WorkPackageView | null;
  currentUserId: number;
  isLoading?: boolean;
}

@Component({
  selector: 'app-views-list-dialog',
  // eslint-disable-next-line @angular-eslint/prefer-standalone -- pre-existing NgModule-declared component; standalone migration is tracked separately (COL-36/COL-37), out of scope for this hotfix
  standalone: false,
  templateUrl: './views-list-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './views-list-dialog.component.scss',
})
export class ViewsListDialogComponent {
  dialogRef = inject<MatDialogRef<ViewsListDialogComponent>>(MatDialogRef);
  data = inject<ViewsListDialogData>(MAT_DIALOG_DATA);

  searchText = '';
  filteredCategorizedViews: CategorizedViews;
  hoveredViewId: number | null = null;
  isLoading = false;
  hasError = false;

  constructor() {
    const data = this.data;

    this.filteredCategorizedViews = { ...data.categorizedViews };
    this.isLoading = data.isLoading || false;
  }

  /**
   * Update views data (called when views are loaded)
   */
  updateViewsData(views: WorkPackageView[], categorizedViews: CategorizedViews, hasError = false): void {
    this.data.views = views;
    this.data.categorizedViews = categorizedViews;
    this.filteredCategorizedViews = { ...categorizedViews };
    this.isLoading = false;
    this.hasError = hasError;
    // Re-apply search filter if active
    if (this.searchText) {
      this.filterViews();
    }
  }

  /**
   * Check if a view is currently active
   */
  isActiveView(view: WorkPackageView): boolean {
    return this.data.activeView?.id === view.id;
  }

  /**
   * Filter views based on search text
   */
  filterViews(): void {
    const search = this.searchText.toLowerCase().trim();

    if (!search) {
      // Reset to all views
      this.filteredCategorizedViews = { ...this.data.categorizedViews };
      return;
    }

    // Filter each category
    this.filteredCategorizedViews = {
      colobboProvided: this.data.views.filter(
        (v) => v.category === ViewCategory.ColobboProvided && v.name.toLowerCase().includes(search)
      ),
      adminPromoted: this.data.views.filter(
        (v) => v.category === ViewCategory.AdminPromoted && v.name.toLowerCase().includes(search)
      ),
      createdByMe: this.data.views.filter(
        (v) =>
          v.category === ViewCategory.UserCreated &&
          v.ownerId === this.data.currentUserId &&
          v.name.toLowerCase().includes(search)
      ),
      createdByOthers: this.data.views.filter(
        (v) =>
          v.category === ViewCategory.UserCreated &&
          v.ownerId !== this.data.currentUserId &&
          v.name.toLowerCase().includes(search)
      ),
    };
  }

  /**
   * Get count of views in a category
   */
  getCategoryCount(category: keyof typeof this.filteredCategorizedViews): number {
    return this.filteredCategorizedViews[category]?.length || 0;
  }

  /**
   * Check if any views exist
   */
  hasAnyViews(): boolean {
    return this.data.views.length > 0;
  }

  /**
   * Select a view and close dialog
   */
  selectView(view: WorkPackageView): void {
    this.dialogRef.close(view);
  }

  /**
   * Close dialog without selection
   */
  close(): void {
    this.dialogRef.close();
  }

  /**
   * Open create new view dialog
   */
  createNewView(): void {
    this.dialogRef.close({ action: 'create' });
  }

  /**
   * Handle drag and drop reordering
   */
  onDrop(event: CdkDragDrop<WorkPackageView[]>, category: keyof typeof this.filteredCategorizedViews): void {
    const viewList = this.filteredCategorizedViews[category];
    moveItemInArray(viewList, event.previousIndex, event.currentIndex);
    
    // TODO: Persist the new order to backend
    console.log('View reordered:', category, viewList.map(v => v.name));
  }

  /**
   * Track hover state
   */
  onViewHover(viewId: number | null): void {
    this.hoveredViewId = viewId;
  }

  /**
   * Check if view is hovered
   */
  isViewHovered(viewId: number): boolean {
    return this.hoveredViewId === viewId;
  }
}
